import React, { useState, useMemo } from 'react';
import {
  Card,
  Typography,
  List,
  Button,
  Tag,
  Badge,
  Avatar,
  Space,
  message,
  Alert,
  Select,
  Input,
  Modal,
  Form,
  Checkbox,
  Radio,
  Divider,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  DollarOutlined,
  UserOutlined,
  SettingOutlined,
  CarOutlined,
  TeamOutlined,
  CrownOutlined,
  ShopOutlined,
  SendOutlined,
  MailOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Notifications = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isSendModalVisible, setIsSendModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [targetType, setTargetType] = useState('all');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch notifications
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['notifications', searchTerm, typeFilter, statusFilter],
    async () => {
      try {
        const response = await api.get('/admin/admin-notifications');
        const { data } = response.data;
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        return []; // Return empty array on error
      }
    },
    {
      refetchInterval: false, // Disable polling - was causing issues
      refetchOnWindowFocus: false,
      staleTime: 30000,
      retry: 1,
    },
  );

  // Fetch unread count with proper configuration
  const { data: unreadCount = 0 } = useQuery(
    'unread-notification-count',
    async () => {
      try {
        const response = await api.get('/admin/admin-notifications/unread-count');
        return response.data?.data?.unread_count || 0;
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
        return 0;
      }
    },
    {
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 30000,
      retry: 1,
    },
  );

  // Fetch notification targets
  const { data: targets = {}, isLoading: targetsLoading } = useQuery(
    ['notification-targets', targetType],
    async () => {
      try {
        const response = await api.get(`/admin/admin-notifications/targets?type=${targetType}`);
        return response.data?.data || {};
      } catch (error) {
        console.error('Failed to fetch notification targets:', error);
        return {};
      }
    },
  );

  // Mark as read mutation
  const markAsReadMutation = useMutation(
    async (notificationId) => {
      const response = await api.post(`/admin/admin-notifications/${notificationId}/mark-read`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Notification marked as read');
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unread-notification-count');
      },
      onError: (error) => {
        message.error(
          'Failed to mark notification as read: ' +
            (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation(
    async () => {
      const response = await api.post('/admin/admin-notifications/mark-all-read');
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('All notifications marked as read');
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unread-notification-count');
      },
      onError: (error) => {
        message.error(
          'Failed to mark all notifications as read: ' +
            (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Delete notification mutation
  const deleteNotificationMutation = useMutation(
    async (notificationId) => {
      const response = await api.delete(`/admin/admin-notifications/${notificationId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Notification deleted successfully');
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('unread-notification-count');
      },
      onError: (error) => {
        console.error('Delete error:', error);
        message.error(
          'Failed to delete notification: ' +
            (error.response?.data?.message || error.message || error.code),
        );
      },
    },
  );

  // Send notification mutation
  const sendNotificationMutation = useMutation(
    async (values) => {
      const response = await api.post('/admin/admin-notifications/send', values);
      return response.data;
    },
    {
      onSuccess: (responseData) => {
        message.success(responseData.message || 'Notification sent successfully');
        setIsSendModalVisible(false);
        form.resetFields();
        setSelectedTargets([]);
        setTargetType('all');
        queryClient.invalidateQueries('notifications');
      },
      onError: (error) => {
        message.error(
          'Failed to send notification: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  const handleMarkAsRead = (notificationId) => {
    markAsReadMutation.mutate(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleSendNotification = (values) => {
    const payload = {
      ...values,
      target_type: targetType,
      target_ids: targetType === 'specific' ? selectedTargets : undefined,
    };
    sendNotificationMutation.mutate(payload);
  };

  const handleTargetTypeChange = (type) => {
    setTargetType(type);
    setSelectedTargets([]);
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      order: <ShopOutlined />,
      customer: <UserOutlined />,
      provider: <TeamOutlined />,
      delivery: <CarOutlined />,
      payment: <DollarOutlined />,
      system: <SettingOutlined />,
      membership: <CrownOutlined />,
    };
    return iconMap[type] || <BellOutlined />;
  };

  const getNotificationColor = (type) => {
    const colorMap = {
      order: '#1890ff',
      customer: '#52c41a',
      provider: '#722ed1',
      delivery: '#13c2c2',
      payment: '#fa8c16',
      system: '#666666',
      membership: '#eb2f96',
    };
    return colorMap[type] || '#1890ff';
  };

  const getNotificationTitle = (notification) => {
    if (notification.data?.user_type === 'customer') {
      return 'New Customer Registered';
    }
    return notification.title;
  };

  const getNotificationLink = (notification) => {
    const { type } = notification;

    switch (type) {
      case 'order':
        return '/orders';
      case 'customer':
        return '/users/customers';
      case 'provider':
        return '/users/providers';
      case 'delivery':
        return '/users/delivery';
      case 'membership':
        return '/membership';
      case 'system':
        return '/dashboard';
      default:
        return '/dashboard';
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already read
    if (!notification.read) {
      try {
        await markAsReadMutation.mutateAsync(notification.id);
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }

    // Navigate to the corresponding page
    const link = getNotificationLink(notification);
    navigate(link);
  };

  const sortedNotifications = useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    return [...notifications].sort((a, b) => {
      // Unread first, then by date
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [notifications]);

  const filteredNotifications = sortedNotifications.filter((notification) => {
    if (!notification || typeof notification !== 'object') return false;

    const matchesSearch =
      !searchTerm ||
      (notification.title && notification.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (notification.message &&
        notification.message.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType = !typeFilter || notification.type === typeFilter;

    const matchesStatus =
      !statusFilter ||
      (statusFilter === 'unread' && !notification.read) ||
      (statusFilter === 'read' && notification.read);

    return matchesSearch && matchesType && matchesStatus;
  });

  const unreadNotifications = filteredNotifications.filter((n) => !n.read);

  if (error) {
    return (
      <Alert
        message="Error Loading Notifications"
        description="Failed to load notifications. Please try again later."
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
        style={{ marginBottom: 24 }}
      />
    );
  }

  return (
    <div>
      <Title level={2}>
        <Badge count={unreadCount} size="small">
          <BellOutlined /> Notifications
        </Badge>
      </Title>

      {/* Statistics Cards */}
      <div style={{ marginBottom: 24, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Card size="small" style={{ flex: 1, minWidth: 200 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#1890ff' }}>
              {unreadCount}
            </div>
            <div style={{ color: '#666' }}>Unread</div>
          </div>
        </Card>
        <Card size="small" style={{ flex: 1, minWidth: 200 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#52c41a' }}>
              {Array.isArray(filteredNotifications)
                ? filteredNotifications.length - unreadNotifications.length
                : 0}
            </div>
            <div style={{ color: '#666' }}>Read</div>
          </div>
        </Card>
        <Card size="small" style={{ flex: 1, minWidth: 200 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#722ed1' }}>
              {Array.isArray(filteredNotifications) ? filteredNotifications.length : 0}
            </div>
            <div style={{ color: '#666' }}>Total</div>
          </div>
        </Card>
      </div>

      <Card>
        {/* Filters and Actions */}
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <Space>
            <Input
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              placeholder="Filter by type"
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="order">Orders</Option>
              <Option value="customer">Customers</Option>
              <Option value="provider">Providers</Option>
              <Option value="delivery">Delivery</Option>
              <Option value="membership">Membership</Option>
              <Option value="system">System</Option>
            </Select>
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="unread">Unread</Option>
              <Option value="read">Read</Option>
            </Select>
          </Space>

          <Space>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => setIsSendModalVisible(true)}
            >
              Send Notification
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
              Refresh
            </Button>
            <Button
              type="default"
              icon={<CheckOutlined />}
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0}
              loading={markAllAsReadMutation.isLoading}
            >
              Mark All Read
            </Button>
          </Space>
        </div>

        {/* Notifications List */}
        <List
          loading={isLoading}
          dataSource={filteredNotifications}
          renderItem={(notification) => (
            <List.Item
              style={{
                backgroundColor: !notification.read ? '#f6ffed' : '#fafafa',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '8px',
                border: !notification.read ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => handleNotificationClick(notification)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = !notification.read ? '#d9f7be' : '#f0f0f0';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = !notification.read ? '#f6ffed' : '#fafafa';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
              actions={[
                !notification.read && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead(notification.id);
                    }}
                    loading={markAsReadMutation.isLoading}
                  >
                    Mark as read
                  </Button>
                ),
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (notification.is_stored) {
                      deleteNotificationMutation.mutate(notification.id);
                    } else {
                      // For generated notifications, just mark as read
                      handleMarkAsRead(notification.id);
                    }
                  }}
                  loading={deleteNotificationMutation.isLoading}
                >
                  {notification.is_stored ? 'Delete' : 'Dismiss'}
                </Button>,
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    style={{ backgroundColor: getNotificationColor(notification.type) }}
                    icon={getNotificationIcon(notification.type)}
                  />
                }
                title={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontWeight: !notification.read ? 'bold' : 'normal' }}>
                      {getNotificationTitle(notification)}
                    </span>
                    <Space>
                      <Tag color={getNotificationColor(notification.type)}>
                        {notification.type?.toUpperCase()}
                      </Tag>
                      {notification.read ? (
                        <Tag color="default">READ</Tag>
                      ) : (
                        <Tag color="processing">UNREAD</Tag>
                      )}
                    </Space>
                  </div>
                }
                description={
                  <div>
                    <p style={{ margin: '4px 0', color: '#666' }}>{notification.message}</p>
                    <span style={{ fontSize: '14px', color: '#999' }}>
                      {dayjs(notification.created_at).format('MMM DD, YYYY [HH:mm]')}
                    </span>
                    {notification.data && (
                      <div style={{ marginTop: '8px' }}>
                        {notification.data.order_id && (
                          <Tag color="blue">Order #{notification.data.order_id}</Tag>
                        )}
                        {notification.data.user_id && (
                          <Tag color="green">User ID: {notification.data.user_id}</Tag>
                        )}
                        {notification.data.amount && (
                          <Tag color="orange">
                            ${parseFloat(notification.data.amount).toFixed(2)}
                          </Tag>
                        )}
                        {notification.data.tier && (
                          <Tag color="purple">{notification.data.tier.toUpperCase()}</Tag>
                        )}
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
          locale={{
            emptyText: 'No notifications found',
          }}
        />
      </Card>

      {/* Send Notification Modal */}
      <Modal
        title="Send Notification"
        open={isSendModalVisible}
        onCancel={() => {
          setIsSendModalVisible(false);
          form.resetFields();
          setSelectedTargets([]);
          setTargetType('all');
        }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSendNotification}>
          <Form.Item
            name="title"
            label="Notification Title"
            rules={[{ required: true, message: 'Please input notification title!' }]}
          >
            <Input placeholder="Enter notification title" />
          </Form.Item>

          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Please input notification message!' }]}
          >
            <TextArea rows={4} placeholder="Enter notification message" />
          </Form.Item>

          <Form.Item label="Target Audience" required>
            <Radio.Group
              value={targetType}
              onChange={(e) => handleTargetTypeChange(e.target.value)}
            >
              <Radio value="all">All Users</Radio>
              <Radio value="customer">Customers Only</Radio>
              <Radio value="provider">Providers Only</Radio>
              <Radio value="delivery">Delivery Personnel Only</Radio>
              <Radio value="specific">Specific Users</Radio>
            </Radio.Group>
          </Form.Item>

          {targetType === 'specific' && (
            <Form.Item
              label="Select Users"
              rules={[{ required: true, message: 'Please select at least one user!' }]}
            >
              <div style={{ marginBottom: 16 }}>
                <Select
                  mode="multiple"
                  placeholder="Select users"
                  value={selectedTargets}
                  onChange={setSelectedTargets}
                  style={{ width: '100%' }}
                  loading={targetsLoading}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {(targets[targetType]?.users || []).map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.name} ({user.type}) - {user.email}
                    </Option>
                  ))}
                </Select>
                <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
                  {targets[targetType]?.count || 0} {targetType} users available
                </div>
              </div>
            </Form.Item>
          )}

          <Form.Item name="priority" label="Priority" initialValue="normal">
            <Radio.Group>
              <Radio value="low">Low</Radio>
              <Radio value="normal">Normal</Radio>
              <Radio value="high">High</Radio>
            </Radio.Group>
          </Form.Item>

          <Divider>Delivery Methods</Divider>

          <Form.Item name="send_push" valuePropName="checked" initialValue={false}>
            <Checkbox>
              <SendOutlined /> Push Notification
            </Checkbox>
          </Form.Item>

          <Form.Item name="send_email" valuePropName="checked" initialValue={false}>
            <Checkbox>
              <MailOutlined /> Email Notification
            </Checkbox>
          </Form.Item>

          <Form.Item name="send_sms" valuePropName="checked" initialValue={false}>
            <Checkbox>
              <MobileOutlined /> SMS Notification
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={sendNotificationMutation.isLoading}
                icon={<SendOutlined />}
              >
                Send Notification
              </Button>
              <Button
                onClick={() => {
                  setIsSendModalVisible(false);
                  form.resetFields();
                  setSelectedTargets([]);
                  setTargetType('all');
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Notifications;
